import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Turn a relative /api/media/... URL into an absolute one the browser can load.
export const mediaUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${BACKEND_URL}${path}`;
};

/**
 * A brand asset, which is not always a media file.
 *
 * `logo_url` is "/api/media/<tenant>/brand/..." for an agency that uploaded
 * one and "/windlebrook-logo.png" for a file that ships in public/. Running
 * the second through mediaUrl points it at the BACKEND, which does not serve
 * it, so the logo 404s and the alt text shows instead. Only /api/ paths
 * belong to the backend.
 */
export const assetUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return path.startsWith("/api/") ? mediaUrl(path) : path;
};

/**
 * What went wrong, in words a customer can act on.
 *
 * FastAPI rejects a request in three different shapes and only two of them
 * were ever read. A `detail` string is ours. A `detail` object carries a
 * `message`. But a body that fails validation before the handler runs comes
 * back as a LIST of field errors, and every caller treated that as
 * unreadable and fell back to something generic.
 *
 * That mattered most on the checkout. `alex@gmail` satisfies the browser's
 * own type=email check, so the field looks fine and nothing is flagged, and
 * Python's validator then refuses it. The buyer was told "Checkout failed.
 * Please try again." on the one screen where we are asking to be paid, and
 * trying again did exactly the same thing.
 */
const FIELD_LABELS = {
  email: "email address",
  agency_name: "agency name",
  contact_name: "your name",
  phone: "phone number",
  postcode: "postcode",
  price: "price",
  amount: "amount",
};

export function errorMessage(err, fallback = "Something went wrong. Please try again.") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    return detail.message || fallback;
  }
  if (Array.isArray(detail) && detail.length) {
    // Name the field. "Invalid" on a form with six boxes on it is not an
    // instruction, and the reason Pydantic gives ("the part after the
    // @-sign is a special-use or reserved name") is not written for the
    // person reading it.
    const named = detail
      .slice(0, 2)
      .map((e) => {
        const field = [...(e?.loc || [])]
          .reverse()
          .find((part) => typeof part === "string" && part !== "body");
        const label = FIELD_LABELS[field] || (field || "").replace(/_/g, " ");
        if (!label) return "";
        return /email/.test(field || "")
          ? `That ${label} doesn't look right. Check it and try again.`
          : `Please check the ${label} and try again.`;
      })
      .filter(Boolean);
    if (named.length) return named.join(" ");
  }
  return fallback;
}

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  config.headers = config.headers || {};

  // A REQUEST THAT BROUGHT ITS OWN CREDENTIALS KEEPS THEM.
  //
  // This used to overwrite `Authorization` unconditionally with whatever
  // staff token was in localStorage. The client portal signs in as a
  // CONTACT and passes its own session token on every call, so the two
  // collided: anybody signed into the CRM in the same browser could not
  // open a portal link at all. The portal's own request went up with the
  // agent's token, came back 401, and the 401 handler wiped the portal
  // session and bounced them to the login screen — every time, for ever.
  //
  // Two people that hits. An agent checking what their vendor can
  // actually see, which is the first thing anybody does with a portal;
  // and an agency owner who is a vendor on one of their own listings.
  const existing = typeof config.headers.get === "function"
    ? config.headers.get("Authorization")
    : config.headers.Authorization;
  if (existing) return config;

  const token = localStorage.getItem("wb_admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
